<template>
  <Loading
    :loading="isLoading"
    loading-text="Loading scheduling setup..."
  >
    <div class="card-layout">
      <div class="flex">
        <div class="w-1/2 flex flex-col">
          <WeeklyHoursSection
            v-model:weekly-hours="weeklyHours"
            :view-mode="viewMode"
            :save-weekly-hours="handleWeeklyHoursSave"
            :duration="defaultAppointmentDuration"
          />

        </div>

        <div class="w-1/2">
          <DateOverrideSection
            :exceptions="rawCustomerSchedule?.exceptions || []"
            @open-dialog="openCreateDialog"
            @edit-exception="handleEditException"
            :delete-exception="handleDeleteException"
          />
        </div>
      </div>



      <div class="pt-8 border-t border-dashed border-[#E5E5E5]">
        <AppointmentSchedulingWindowSection
          v-model:soonest-value="soonestValue"
          v-model:soonest-unit="soonestUnit"
          v-model:furthest-value="furthestValue"
          v-model:furthest-unit="furthestUnit"
          :default-appointment-duration="defaultAppointmentDuration"
        />
      </div>

      <div class="pt-8 border-t border-dashed border-[#E5E5E5]">
        <AppointmentDurationSection
          v-model:duration="defaultAppointmentDuration"
          :soonest-value="soonestValue"
          :soonest-unit="soonestUnit"
          :furthest-value="furthestValue"
          :furthest-unit="furthestUnit"
        />
      </div>


      <OverrideDateDialog
        v-if="showDialog"
        :visible="showDialog"
        :duration="defaultAppointmentDuration"
        :editing-exception="editingException"
        :save-exception="handleAddException"
        @close="handleDialogClose"
      />
    </div>
  </Loading>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import OverrideDateDialog from './setup-scheduling/OverrideDateDialog.vue';
import WeeklyHoursSection from './setup-scheduling/WeeklyHoursSection.vue';
import DateOverrideSection from './setup-scheduling/DateOverrideSection.vue';
import AppointmentSchedulingWindowSection from './setup-scheduling/AppointmentSchedulingWindowSection.vue';
import AppointmentDurationSection from './setup-scheduling/AppointmentDurationSection.vue';
import Loading from '@/NewLibrary/Loading/Loading.vue';

const store = useStore();
const rawCustomerSchedule = computed(() => store.getters['calendar/rawCustomerSchedule']);

const isLoading = ref(false);
const showDialog = ref(false);
const editingException = ref(null);
const viewMode = ref('view');
const soonestValue = ref('24');
const soonestUnit = ref('Hours');
const furthestValue = ref('30');
const furthestUnit = ref('Days');
const defaultAppointmentDuration = ref('60');

const weekdayMap = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const weekdayNumToStr = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const weeklyHours = ref({
  Sunday: [],
  Monday: [{ start: '', end: '' }],
  Tuesday: [{ start: '', end: '' }],
  Wednesday: [{ start: '', end: '' }],
  Thursday: [{ start: '', end: '' }],
  Friday: [{ start: '', end: '' }],
  Saturday: [],
});

function formatWeeklyHours() {
  const result = [];
  for (const [day, slots] of Object.entries(weeklyHours.value)) {
    const weekday = weekdayMap[day];
    slots.forEach(slot => {
      if (slot.start && slot.end) {
        result.push({
          weekday,
          start_time: displayToTimeStr(slot.start),
          end_time: displayToTimeStr(slot.end),
        });
      }
    });
  }
  return result;
}

function fillWeeklyHoursFromSchedule(schedule) {
  for (const key in weeklyHours.value) {
    weeklyHours.value[key] = [];
  }
  if (!schedule || !Array.isArray(schedule.weekly_schedule)) return;
  schedule.weekly_schedule.forEach(item => {
    const dayStr = weekdayNumToStr[item.weekday];
    if (dayStr) {
      weeklyHours.value[dayStr].push({
        start: timeStrToDisplay(item.start_time),
        end: timeStrToDisplay(item.end_time),
      });
    }
  });
}

function timeStrToDisplay(timeStr) {
  if (!timeStr) return 'N/A';
  const time = moment(timeStr, 'HH:mm:ss');
  if (!time.isValid()) return 'N/A';
  return time.format('hh:mma');
}

function displayToTimeStr(display) {
  if (!display) return '';
  const time = moment(display, 'hh:mma');
  if (!time.isValid()) return '';
  return time.format('HH:mm:00');
}

function normalizeRuleValue(value, fallback = '0') {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
}

function getSoonestFromMinutes(minutes) {
  const normalizedMinutes = Number(minutes);
  if (!Number.isFinite(normalizedMinutes) || normalizedMinutes < 0) {
    return { value: '24', unit: 'Hours' };
  }
  return { value: String(normalizedMinutes / 60), unit: 'Hours' };
}

function getFurthestFromDays(days) {
  const normalizedDays = Number(days);
  if (!Number.isFinite(normalizedDays) || normalizedDays < 0) {
    return { value: '30', unit: 'Days' };
  }
  if (normalizedDays % 30 === 0) {
    return { value: String(normalizedDays / 30), unit: 'Months' };
  }
  if (normalizedDays % 7 === 0) {
    return { value: String(normalizedDays / 7), unit: 'Weeks' };
  }
  return { value: String(normalizedDays), unit: 'Days' };
}

async function loadUserSettings() {
  const userSettings = await store.dispatch('calendar/getUserSettings');
  const soonest = getSoonestFromMinutes(userSettings?.min_notice_minutes);
  const furthest = getFurthestFromDays(userSettings?.max_advance_days);
  const duration = normalizeRuleValue(userSettings?.default_appointment_duration, '60');

  soonestValue.value = soonest.value;
  soonestUnit.value = soonest.unit;
  furthestValue.value = furthest.value;
  furthestUnit.value = furthest.unit;
  defaultAppointmentDuration.value = duration;
}

async function handleAddException(newExceptions) {
  const userInfo = store.state.login.userInfo;
  if (editingException.value?.exception_id) {
    const [nextException] = newExceptions;
    if (nextException) {
      await store.dispatch('calendar/updateScheduleException', {
        exception_id: editingException.value.exception_id,
        date: nextException.date,
        start_time: nextException.start_time,
        end_time: nextException.end_time,
        is_available: nextException.is_available,
      });
    }
  } else {
    await store.dispatch('calendar/addBatchScheduleException', {
      clinicId: userInfo.clinic_id,
      customerId: userInfo.customer_id,
      scheduleExceptionInput: {
        exceptions: newExceptions
      }
    });
  }
  await store.dispatch('calendar/fetchCustomerSchedule', {
    clinicId: userInfo.clinic_id,
    customerId: userInfo.customer_id,
  });
  editingException.value = null;
  showDialog.value = false;
}

async function handleDeleteException(exception) {
  const userInfo = store.state.login.userInfo;
  await store.dispatch('calendar/deleteScheduleException', {
    exception_id: exception.exception_id,
  });
  await store.dispatch('calendar/fetchCustomerSchedule', {
    clinicId: userInfo.clinic_id,
    customerId: userInfo.customer_id,
  });
}

function openCreateDialog() {
  editingException.value = null;
  showDialog.value = true;
}

function handleEditException(exception) {
  editingException.value = { ...exception };
  showDialog.value = true;
}

function handleDialogClose() {
  showDialog.value = false;
  editingException.value = null;
}

watch(rawCustomerSchedule, (val) => {
  fillWeeklyHoursFromSchedule(val);
}, { immediate: true });

async function handleWeeklyHoursSave(shouldSave) {
  if (!shouldSave) {
    viewMode.value = 'edit';
    return;
  }

  const userInfo = store.state.login.userInfo;
  const weekly_schedule = formatWeeklyHours();
  await store.dispatch('calendar/updateWorkingHours', {
    clinicId: userInfo.clinic_id,
    customerId: userInfo.customer_id,
    workingHoursInput: { weekly_schedule }
  });
  await store.dispatch('calendar/fetchCustomerSchedule', {
    clinicId: userInfo.clinic_id,
    customerId: userInfo.customer_id,
  });
  fillWeeklyHoursFromSchedule(rawCustomerSchedule.value);
  viewMode.value = 'view';
}

onMounted(async () => {
  const userInfo = store.state.login.userInfo;
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('calendar/fetchCustomerSchedule', {
        clinicId: userInfo.clinic_id,
        customerId: userInfo.customer_id,
      }),
      loadUserSettings(),
    ]);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
.card-layout {
  /* display: flex; */
  background-color: #FFFFFF;
  border-radius: 10px;
  padding: 40px;
  font-family: 'Inter';
  box-shadow: 0px 1px 10px 0px rgba(74, 74, 74, 0.08);
}
</style>